import React, { useRef, useEffect } from 'react';

// ── Demo ride data (static, always looks populated) ──────────────────────────
const DEMO_RIDES = [
  {
    id: 1,
    from: 'Harvard Yard',
    to: 'MIT Campus',
    driver: 'Jordan M.',
    initials: 'JM',
    color: '#6366f1',
    time: 'Mon 7:00 AM',
    seats: 2,
    price: '$3',
    stars: 4.8,
  },
  {
    id: 2,
    from: 'Northeastern',
    to: 'Boston University',
    driver: 'Sarah K.',
    initials: 'SK',
    color: '#10b981',
    time: 'Mon 6:30 AM',
    seats: 1,
    price: '$2',
    stars: 5.0,
  },
  {
    id: 3,
    from: 'MIT Campus',
    to: 'Tufts University',
    driver: 'Mike R.',
    initials: 'MR',
    color: '#f59e0b',
    time: 'Mon 8:00 AM',
    seats: 3,
    price: '$2.50',
    stars: 4.9,
  },
];

// ── Mini ride card (no context needed) ───────────────────────────────────────
function MiniCard({ ride }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.10)',
      borderRadius: 10,
      padding: '10px 12px',
      marginBottom: 8,
      backdropFilter: 'blur(4px)',
    }}>
      {/* Route */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 3 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e' }} />
          <div style={{ width: 1.5, height: 14, background: 'rgba(255,255,255,0.2)', margin: '2px 0' }} />
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#3b82f6' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#fff', marginBottom: 6, lineHeight: 1.2 }}>
            {ride.from}
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.2 }}>
            {ride.to}
          </div>
        </div>
      </div>

      {/* Meta */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>🕐 {ride.time}</span>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>💺 {ride.seats} left</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#3b82f6' }}>{ride.price}</span>
      </div>

      {/* Driver */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.07)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            background: ride.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 8, fontWeight: 700, color: '#fff',
          }}>
            {ride.initials}
          </div>
          <span style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>
            {ride.driver}
          </span>
        </div>
        <span style={{ fontSize: 10, color: '#fbbf24' }}>
          ★ {ride.stars}
        </span>
      </div>
    </div>
  );
}

// ── Phone frame with mouse-parallax tilt ─────────────────────────────────────
// rotationRef: optional ref from parent { x: 0, y: 15 } degrees for GSAP control
export default function PhoneMockup({ rotationRef, style = {} }) {
  const phoneRef = useRef(null);
  // Internal target for smooth mousemove lerp
  const target   = useRef({ x: 5, y: -15 });
  const current  = useRef({ x: 5, y: -15 });
  const rafId    = useRef(null);

  useEffect(() => {
    const el = phoneRef.current;
    if (!el) return;

    const onMove = (e) => {
      const cx = window.innerWidth  / 2;
      const cy = window.innerHeight / 2;
      const dx = (e.clientX - cx) / cx; // -1 to 1
      const dy = (e.clientY - cy) / cy;
      target.current.y = -15 + dx * 8;   // base tilt -15° ± 8°
      target.current.x =   5 - dy * 5;   //             5° ± 5°
    };

    const onLeave = () => {
      target.current.x = 5;
      target.current.y = -15;
    };

    // Lerp loop for smooth interpolation
    const lerp = (a, b, t) => a + (b - a) * t;
    const loop = () => {
      current.current.x = lerp(current.current.x, target.current.x, 0.07);
      current.current.y = lerp(current.current.y, target.current.y, 0.07);

      // If a GSAP-controlled rotationRef exists, blend it in
      const ry = rotationRef ? rotationRef.current : current.current.y;
      const rx = current.current.x;

      if (el) {
        el.style.transform =
          `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg)`;
      }
      rafId.current = requestAnimationFrame(loop);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);
    rafId.current = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(rafId.current);
    };
  }, [rotationRef]);

  return (
    <div
      ref={phoneRef}
      style={{
        width: 260,
        height: 520,
        borderRadius: 36,
        background: 'linear-gradient(145deg, #1a2540, #111827)',
        border: '2px solid rgba(255,255,255,0.12)',
        boxShadow: `
          0 0 0 1px rgba(59,130,246,0.2),
          0 30px 80px rgba(0,0,0,0.6),
          0 0 60px rgba(59,130,246,0.12),
          inset 0 1px 0 rgba(255,255,255,0.08)
        `,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        transformOrigin: 'center center',
        willChange: 'transform',
        flexShrink: 0,
        ...style,
      }}
    >
      {/* Notch */}
      <div style={{
        position: 'absolute', top: 10, left: '50%',
        transform: 'translateX(-50%)',
        width: 80, height: 22,
        background: '#0d1117',
        borderRadius: 12,
        zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
      }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#1d2535' }} />
        <div style={{ width: 14, height: 6, borderRadius: 3, background: '#1d2535' }} />
      </div>

      {/* Screen */}
      <div style={{
        flex: 1,
        marginTop: 40,
        marginBottom: 24,
        marginLeft: 6,
        marginRight: 6,
        borderRadius: 28,
        overflow: 'hidden',
        background: '#0d1b2e',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* App bar */}
        <div style={{
          padding: '12px 14px 8px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
            Browse Rides
          </div>
          {/* Search bar */}
          <div style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 8,
            padding: '6px 10px',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>🔍</span>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>
              Where are you going?
            </span>
          </div>
        </div>

        {/* Cards */}
        <div style={{ flex: 1, overflowY: 'hidden', padding: '10px 10px 4px' }}>
          {DEMO_RIDES.map(ride => (
            <MiniCard key={ride.id} ride={ride} />
          ))}
        </div>
      </div>

      {/* Home bar */}
      <div style={{
        position: 'absolute', bottom: 8, left: '50%',
        transform: 'translateX(-50%)',
        width: 80, height: 4,
        background: 'rgba(255,255,255,0.25)',
        borderRadius: 2,
      }} />

      {/* Edge glow */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 36,
        background: 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />
    </div>
  );
}
